import { Skeleton, Table } from "antd";
import { getInitials } from "helpers/initials";
import { ColumnProps } from "antd/lib/table";
import { UserRoleEnum } from "config/enum";
import {
  DORMITORY_MASTER_ROUTE,
  HOME_CONTROLLER_DETAIL_ROUTE,
  ROOM_DETAIL_ROUTE,
} from "config/route-consts";
import { DormitoryMember } from "models/DormitoryMember";
import { HomeController } from "models/HomeController";
import { Room } from "models/Room";
import React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { dormitoryRepository } from "repositories/dormitory-repository";
import { authService } from "services/auth-services";
import { ActivityLogModal, useActivityLogModal } from "./ActivityLogModal";
import "./DormitoryDetail.scss";
import useDormitoryDetail from "./DormitoryDetailHook";
import DormitoryRecentActivity from "./DormitoryRecentActivity";
import { SupporterModal, useSupporterModal } from "./SupporterModal";

// Trạng thái HC: 1 = Online, 2 = Offline (giống trang danh sách HC).
const HC_ONLINE = 1;


function HcStatusBadge({ hc }: { hc: HomeController }) {
  const status = hc.homeControllerActiveStatus;
  if (!status) return null;
  return (
    <span className={`home-controller__status home-controller__status--${status.id}`}>
      {status.name}
    </span>
  );
}

// Chi tiết Nhà, phương án A (bỏ bản đồ): đầu trang + dải số liệu + một màn đủ việc.
function DormitoryDetail() {
  const history = useHistory();

  const { model, setModel, handleGoEndUser, isDeleted } = useDormitoryDetail(
    dormitoryRepository.get
  );

  const { isRoleSupportFeature } = authService.useRole();
  const canAddSupporter = isRoleSupportFeature([
    UserRoleEnum.Admin,
    UserRoleEnum.Operate,
    UserRoleEnum.Supporter,
  ]);

  const {
    visibleSupporterModal,
    openSupporterModal,
    cancelSupporterModal,
    saveSupporterModal,
  } = useSupporterModal();

  const {
    openActivityLogModal,
    visibleActivityLogModal,
    cancelActivityLogModal,
  } = useActivityLogModal();

  const handleGoHomeController = React.useCallback(
    (id: string | number) => {
      if (id) history.push(`${HOME_CONTROLLER_DETAIL_ROUTE}?id=${id}`);
    },
    [history]
  );

  const handleGoRoom = React.useCallback(
    (id: string | number) => {
      if (id) history.push(`${ROOM_DETAIL_ROUTE}?id=${id}`);
    },
    [history]
  );

  const homeControllers = model.homeControllers ?? [];
  const rooms = model.rooms ?? [];
  const members = model.members ?? [];
  const onlineCount = homeControllers.filter(
    (hc) => hc.homeControllerActiveStatus?.id === HC_ONLINE
  ).length;
  const offlineCount = homeControllers.length - onlineCount;
  const owner = model.endUser;

  const homeControllerColumns: ColumnProps<HomeController>[] = React.useMemo(
    () => [
      {
        title: "Home Controller",
        key: "name",
        render: (_value: string, hc) => (
          <div className="dorm-detail__cell">
            <div className="dorm-detail__strong dorm-detail__truncate">{hc.name || "—"}</div>
            <div className="dorm-detail__sub dorm-detail__mono">{hc.macAddress}</div>
          </div>
        ),
      },
      {
        title: "Trạng thái",
        key: "status",
        render: (_value: string, hc) => <HcStatusBadge hc={hc} />,
      },
      {
        title: "Địa chỉ IP",
        dataIndex: "ipAddress",
        render: (value: string) => <span className="tabular-nums">{value}</span>,
      },
      {
        title: "Phiên bản FW",
        dataIndex: "version",
        render: (value: string) => <span className="tabular-nums">{value}</span>,
      },
      {
        title: "Thiết bị",
        dataIndex: "deviceQuantity",
        render: (value: number) => <span className="tabular-nums">{value}</span>,
      },
    ],
    []
  );

  if (isDeleted) {
    return (
      <div className="dorm-detail">
        <Link to={DORMITORY_MASTER_ROUTE} className="page-back">
          <i className="tio-chevron_left" aria-hidden />
          Danh sách nhà
        </Link>
        <section className="dorm-detail__card dorm-detail__deleted">
          <span className="dorm-detail__deleted-icon">
            <i className="tio-home_outlined" />
          </span>
          <div>
            <div className="dorm-detail__deleted-title">Nhà đã bị xoá</div>
            <div className="dorm-detail__sub">Nhà này đã bị xoá khỏi hệ thống hoặc không tồn tại.</div>
          </div>
          <Link className="dorm-detail__btn" to={DORMITORY_MASTER_ROUTE}>
            Về danh sách nhà
          </Link>
        </section>
      </div>
    );
  }

  // Chưa có dữ liệu thì hiện khung xám thay vì các khối "chưa có…" nhấp nháy.
  if (!model.id) {
    return (
      <div className="dorm-detail">
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    );
  }

  return (
    <div className="dorm-detail">
      {/* ----- Đầu trang ----- */}
      <div className="dorm-detail__head">
        <div className="dorm-detail__head-text">
          <Link to={DORMITORY_MASTER_ROUTE} className="page-back">
            <i className="tio-chevron_left" aria-hidden />
            Danh sách nhà
          </Link>
          <h1 className="dorm-detail__title">{model.name || "—"}</h1>
          {model.address && <p className="dorm-detail__meta">{model.address}</p>}
        </div>
        <div className="dorm-detail__actions">
          <button type="button" className="dorm-detail__btn" onClick={openActivityLogModal}>
            <i className="tio-history" />
            Lịch sử hoạt động
          </button>
          {canAddSupporter && (
            <button
              type="button"
              className="dorm-detail__btn dorm-detail__btn--primary"
              onClick={openSupporterModal}
            >
              <i className="tio-user_add" />
              Thêm tài khoản hỗ trợ
            </button>
          )}
        </div>
      </div>

      {/* ----- Dải số liệu ----- */}
      <section className="dorm-detail__card dorm-detail__strip">
        {owner ? (
          <button
            type="button"
            className="dorm-detail__owner"
            onClick={handleGoEndUser(model.endUserId ?? owner.id)}
          >
            {owner.avatar ? (
              <img className="dorm-detail__avatar dorm-detail__avatar--lg" src={owner.avatar} alt="" />
            ) : (
              <span className="dorm-detail__avatar dorm-detail__avatar--lg">
                {getInitials(owner.displayName)}
              </span>
            )}
            <div className="dorm-detail__cell">
              <div className="dorm-detail__sub">Chủ nhà</div>
              <div className="dorm-detail__strong dorm-detail__truncate">{owner.displayName || "—"}</div>
              <div className="dorm-detail__sub dorm-detail__truncate">
                {owner.phoneNumber || owner.email}
              </div>
            </div>
            <i className="tio-chevron_right dorm-detail__chevron" />
          </button>
        ) : (
          <div className="dorm-detail__stat">
            <span className="dorm-detail__sub">Chủ nhà</span>
            <span className="dorm-detail__muted">Chưa có chủ nhà</span>
          </div>
        )}
        <div className="dorm-detail__stat">
          <span className="dorm-detail__stat-label">Home Controller</span>
          <span className="dorm-detail__stat-value">
            {homeControllers.length > 0 ? (
              <>
                {onlineCount}
                <small>/{homeControllers.length} online</small>
              </>
            ) : (
              0
            )}
          </span>
          {offlineCount > 0 && (
            <span className="status-badge status-badge--error">{offlineCount} offline</span>
          )}
        </div>
        <div className="dorm-detail__stat">
          <span className="dorm-detail__stat-label">Phòng</span>
          <span className="dorm-detail__stat-value">{rooms.length}</span>
        </div>
        <div className="dorm-detail__stat">
          <span className="dorm-detail__stat-label">Thành viên</span>
          <span className="dorm-detail__stat-value">{members.length}</span>
        </div>
      </section>

      <div className="dorm-detail__grid">
        {/* ----- Cột chính ----- */}
        <div className="dorm-detail__stack">
          <section className="dorm-detail__card">
            <div className="dorm-detail__card-head">
              <h2 className="dorm-detail__card-title">
                Home Controller <span className="dorm-detail__count">· {homeControllers.length}</span>
              </h2>
            </div>
            {homeControllers.length === 0 ? (
              <p className="dorm-detail__empty">Nhà chưa gắn Home Controller nào</p>
            ) : (
              <>
                <Table
                  className="dorm-detail__table"
                  rowKey="id"
                  columns={homeControllerColumns}
                  dataSource={homeControllers}
                  pagination={false}
                  scroll={{ x: "max-content" }}
                  onRow={(hc) => ({ onClick: () => handleGoHomeController(hc.id) })}
                />
                <ul className="dorm-detail__rows dorm-detail__rows--mobile">
                  {homeControllers.map((hc) => (
                    <li key={hc.id} onClick={() => handleGoHomeController(hc.id)}>
                      <span className="dorm-detail__obj-icon">
                        <i className="tio-router" />
                      </span>
                      <div className="dorm-detail__grow">
                        <div className="dorm-detail__strong dorm-detail__truncate">{hc.name || "—"}</div>
                        <div className="dorm-detail__sub">
                          {[hc.ipAddress, hc.version && `FW ${hc.version}`, `${hc.deviceQuantity ?? 0} thiết bị`]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      <HcStatusBadge hc={hc} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="dorm-detail__card">
            <div className="dorm-detail__card-head">
              <h2 className="dorm-detail__card-title">
                Phòng <span className="dorm-detail__count">· {rooms.length}</span>
              </h2>
            </div>
            {rooms.length === 0 ? (
              <p className="dorm-detail__empty">Chủ nhà chưa tạo phòng nào trên app</p>
            ) : (
              <ul className="dorm-detail__rows">
                {rooms.map((room: Room) => (
                  <li key={room.id} onClick={() => handleGoRoom(room.id)}>
                    <span className="dorm-detail__obj-icon">
                      <i className="tio-door" />
                    </span>
                    <div className="dorm-detail__grow">
                      <div className="dorm-detail__strong dorm-detail__truncate">{room.name || "—"}</div>
                      <div className="dorm-detail__sub">
                        {room.devices?.length ?? 0} thiết bị ·{" "}
                        {room.scenes?.length ? `${room.scenes.length} cảnh` : "chưa có cảnh"}
                      </div>
                    </div>
                    <i className="tio-chevron_right dorm-detail__chevron" />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* ----- Cột phụ ----- */}
        <div className="dorm-detail__stack">
          <section className="dorm-detail__card">
            <div className="dorm-detail__card-head">
              <h2 className="dorm-detail__card-title">
                Thành viên <span className="dorm-detail__count">· {members.length}</span>
              </h2>
            </div>
            {members.length === 0 ? (
              <p className="dorm-detail__empty">Nhà chưa có thành viên nào</p>
            ) : (
              <ul className="dorm-detail__rows">
                {members.map((member: DormitoryMember) => (
                  <li key={member.endUserId} onClick={handleGoEndUser(member.endUserId)}>
                    {member.endUser?.avatar ? (
                      <img className="dorm-detail__avatar" src={member.endUser.avatar} alt="" />
                    ) : (
                      <span className="dorm-detail__avatar">
                        {getInitials(member.endUser?.displayName)}
                      </span>
                    )}
                    <div className="dorm-detail__grow">
                      <div className="dorm-detail__strong dorm-detail__truncate">
                        {member.endUser?.displayName || "—"}
                      </div>
                      <div className="dorm-detail__sub dorm-detail__truncate">
                        {member.endUser?.phoneNumber || member.endUser?.email}
                      </div>
                    </div>
                    {member.endUserRole?.name && (
                      <span className="dorm-detail__role">{member.endUserRole.name}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <DormitoryRecentActivity dormitoryId={model.id} onViewAll={openActivityLogModal} />
        </div>
      </div>

      <SupporterModal
        setDormitory={setModel}
        visibleSupporterModal={visibleSupporterModal}
        cancelSupporterModal={cancelSupporterModal}
        saveSupporterModal={saveSupporterModal}
      />
      <ActivityLogModal
        visibleActivityLogModal={visibleActivityLogModal}
        cancelActivityLogModal={cancelActivityLogModal}
      />
    </div>
  );
}

export default DormitoryDetail;
